import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, type MutableRefObject } from 'react';
import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { Input } from '@/ui/input/input';
import { Stack } from '@/ui/line/line';
import { BorderedTextArea } from '@/ui/textarea/bordered-text-area';

import {
  contactInnovationTeamValidationSchema,
  type ContactInnovationTeamFormData,
} from './contact-innovation-team-validation-schema';
import { notify } from '@/ui/snackbar/notify';
import { captureExceptionSafely } from '@/helpers/capture-exception-safely';
import { useSendInquiry } from './use-send-inquiry.mutation';
import { Alert } from '@/components/alert/alert';
import { SpotlightButton } from '@/ui/spotlight';
import { useSpotlightContactFormTracking } from '@/features/spotlight/tracking/use-spotlight-contact-form-tracking';
import type { SpotlightContactFormCloseTracking } from '@/features/spotlight/tracking/use-spotlight-contact-form-tracking';
import { useCurrentUser } from '@/user/use-current-user.query';

interface ContactInnovationTeamFormProps {
  accountName: string;
  closeTrackingRef?: MutableRefObject<SpotlightContactFormCloseTracking | null>;
  companyName: string;
  organizationId: string;
  useSpotlightInquiry?: boolean;
}

export const ContactInnovationTeamForm = ({
  accountName,
  closeTrackingRef,
  companyName,
  organizationId,
  useSpotlightInquiry = false,
}: ContactInnovationTeamFormProps) => {
  const { t } = useTranslation(['contact-innovation-team', 'default']);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    mutateAsync: submitSendInquiry,
    isPending,
    isSuccess,
  } = useSendInquiry();
  const currentUser = useCurrentUser();
  const userId = currentUser.data?.id ?? null;

  const { handleFieldInteraction, trackFormAbandoned, trackFormSubmitted } =
    useSpotlightContactFormTracking({
      companyId: organizationId,
      companyName,
      userId,
    });

  useEffect(() => {
    if (closeTrackingRef === undefined) {
      return;
    }

    closeTrackingRef.current = { trackAbandoned: trackFormAbandoned };

    return () => {
      closeTrackingRef.current = null;
    };
  }, [closeTrackingRef, trackFormAbandoned]);

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isDirty, isValid },
  } = useForm<ContactInnovationTeamFormData>({
    defaultValues: {
      department: '',
      email: '',
      message: '',
      name: '',
    },
    mode: 'onChange',
    resolver: zodResolver(contactInnovationTeamValidationSchema),
  });

  const onSubmit = async (data: ContactInnovationTeamFormData) => {
    try {
      await submitSendInquiry({
        department: data.department,
        email: data.email,
        message: data.message,
        name: data.name,
        organizationId,
        useSpotlightInquiry,
      });
      trackFormSubmitted();
      setIsSubmitted(true);
    } catch (error) {
      captureExceptionSafely(error);
      notify({ message: t('default:unknownError') });
      setIsSubmitted(false);
    }
  };

  if (isSubmitted && isSuccess) {
    return (
      <FormContainer>
        <Stack fullWidth gap="24px">
          <SuccessHeader>
            <SuccessIcon data-testid="contact-innovation-team-success-icon">
              ✓
            </SuccessIcon>
            <SuccessTitle data-testid="contact-innovation-team-success-title">
              {t('success.title')}
            </SuccessTitle>
            <SuccessDescription data-testid="contact-innovation-team-success-description">
              {t('success.description', { accountName })}
            </SuccessDescription>
          </SuccessHeader>
        </Stack>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Stack fullWidth gap="24px">
        <FormAlert
          type="info"
          messages={[t('alert.message', { accountName })]}
        />
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Stack fullWidth gap="20px" margin="0 0 24px 0">
            <Stack fullWidth gap="8px">
              <FieldLabel htmlFor="contact-innovation-team-name">
                {t('form.name.label')}
              </FieldLabel>
              <Input
                id="contact-innovation-team-name"
                data-testid="contact-innovation-team-name"
                placeholder={t('form.name.placeholder')}
                {...register('name')}
                disabled={isPending}
                error={errors?.name?.message}
                onFocus={handleFieldInteraction}
                onKeyDown={handleFieldInteraction}
              />
            </Stack>

            <Stack fullWidth gap="8px">
              <FieldLabel htmlFor="contact-innovation-team-email">
                {t('form.email.label')}
              </FieldLabel>
              <Input
                id="contact-innovation-team-email"
                data-testid="contact-innovation-team-email"
                type="email"
                placeholder={t('form.email.placeholder')}
                {...register('email')}
                disabled={isPending}
                error={errors?.email?.message}
                onFocus={handleFieldInteraction}
                onKeyDown={handleFieldInteraction}
              />
            </Stack>

            <Stack fullWidth gap="8px">
              <FieldLabel htmlFor="contact-innovation-team-department">
                {t('form.department.label')}
              </FieldLabel>
              <Input
                id="contact-innovation-team-department"
                data-testid="contact-innovation-team-department"
                placeholder={t('form.department.placeholder')}
                {...register('department')}
                disabled={isPending}
                error={errors?.department?.message}
                onFocus={handleFieldInteraction}
                onKeyDown={handleFieldInteraction}
              />
            </Stack>

            <Stack fullWidth gap="8px">
              <FieldLabel htmlFor="contact-innovation-team-message">
                {t('form.message.label')}
              </FieldLabel>
              <Controller
                control={control}
                name="message"
                render={({ field }) => (
                  <BorderedTextArea
                    id="contact-innovation-team-message"
                    data-testid="contact-innovation-team-message"
                    height={120}
                    placeholder={t('form.message.placeholder', { companyName })}
                    resize="vertical"
                    value={field.value ?? null}
                    onChange={newValue => {
                      handleFieldInteraction();
                      field.onChange(newValue);
                    }}
                    onBlur={() => {
                      handleFieldInteraction();
                      field.onBlur();
                    }}
                    error={Boolean(errors?.message)}
                  />
                )}
              />
              {errors?.message?.message ? (
                <FieldError>{errors.message.message}</FieldError>
              ) : null}
            </Stack>
          </Stack>

          <Flexbox fullWidth>
            <SpotlightButton
              disabled={!isDirty || !isValid || isPending}
              fullWidth
              height={36}
              isLoading={isPending}
              textCenter
              variant="primary"
            >
              {t('actions.submit')}
            </SpotlightButton>
          </Flexbox>
        </Form>
      </Stack>
    </FormContainer>
  );
};

const FormContainer = styled(Flexbox)`
  max-width: 500px;
  padding: 32px 12px 20px 12px;
  width: 100%;
`;

const FieldError = styled('span')`
  color: ${({ theme }) => theme.colors.system.lowFit};
  font-size: 12px;
`;

const Form = styled('form')`
  width: 100%;
`;

const FieldLabel = styled('label')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-size: 14px;
  font-weight: 600;
`;

const SuccessHeader = styled('div')`
  padding: 20px 0;
  text-align: center;
`;

const SuccessIcon = styled('div')`
  color: ${({ theme }) => theme.colors.blue.c6};
  font-size: 48px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const SuccessTitle = styled('h2')`
  color: ${({ theme }) => theme.colors.basics.black};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 12px 0;
`;

const SuccessDescription = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
`;

const FormAlert = styled(Alert)`
  font-weight: 500;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 14px;
`;
